export const SEARCH_DRAWER_EVENT = Object.freeze({
  event: 'lp_click_top_search',
  lp_track: 'click_top_search',
  lp_track_context: 'header',
  lp_track_source: 'vendor',
})

export const ADD_TO_CART_SUCCESS_EVENT = Object.freeze({
  event: 'lp_custom_add_to_cart_success',
  lp_track: 'custom_add_to_cart',
  lp_track_source: 'vendor',
  lp_tracking_owner: 'impact_variant_add',
})

export const FAQ_CLICK_EVENT = Object.freeze({
  event: 'lp_click_faq',
  lp_track: 'click_faq',
  lp_track_context: 'pdp_faq',
  lp_track_source: 'vendor',
})

export const KLAVIYO_SUBSCRIBE_SUCCESS_EVENT = Object.freeze({
  event: 'lp_subscribe_success',
  lp_track: 'subscribe',
  lp_track_source: 'vendor',
  lp_tracking_owner: 'klaviyo_forms_submit',
})

export const NEWSLETTER_SUBSCRIBE_INTENT_EVENT = Object.freeze({
  event: 'lp_subscribe_intent',
  lp_track: 'subscribe',
  lp_track_context: 'footer',
  lp_track_source: 'vendor',
  lp_tracking_owner: 'shopify_newsletter_submit',
})

export const HEADER_CATEGORY_SELECT_EVENTS = Object.freeze({
  select_top_category: Object.freeze({
    event: 'lp_select_top_category',
    lp_track: 'select_top_category',
  }),
  select_second_category: Object.freeze({
    event: 'lp_select_second_category',
    lp_track: 'select_second_category',
  }),
})

// Backward-compatible aliases for consumers that imported the phase-seven names.
export const COLLECTION_QUICK_ADD_SUCCESS_EVENT = ADD_TO_CART_SUCCESS_EVENT
export const PDP_ADD_SUCCESS_EVENT = ADD_TO_CART_SUCCESS_EVENT

const listenerStates = new WeakMap()
const searchDrawerSelector = 'search-drawer#search-drawer'
const headerSearchHookSelector =
  '[data-lp-track="click_top_search"][data-lp-track-context="header"][data-lp-track-source="vendor"]'
const collectionCardSelector = '.shopify-section--main-collection .product-card'
const cartCardSelector =
  '.shopify-section--main-cart ~ .shopify-section--featured-collection .product-card'
const homeCardSelector = '.shopify-section--featured-collection .product-card'
const recommendationCardSelectors = Object.freeze([
  '.shopify-section--product-recommendations .product-card',
  '.lp5-compatible-products .product-card',
])
const directQuickAddContainerSelector = '.product-card__quick-buy'
const productFormSelector = 'form[is="product-form"]'
const mainProductRootSelector = '.shopify-section--main-product'
const figmaRootSelector = 'main-product-figma[data-main-product-figma]'
const addHookSelector = '[data-lp-track="custom_add_to_cart"][data-lp-track-source="vendor"]'
const faqHookSelector = '[data-lp-track="click_faq"][data-lp-track-source="vendor"]'
const faqQuestionSelector = ':scope > div:first-child > div:first-child, :scope > span:first-child'
const subscribeHookSelector = '[data-lp-track="subscribe"][data-lp-track-source="vendor"]'
const newsletterFormSelector =
  '.shopify-section-group-footer-group.shopify-section--newsletter form[action*="/contact"], form.footer__newsletter-form[action*="/contact"]'
const klaviyoFooterContainerSelector = '.shopify-section-group-footer-group, .footer'
const klaviyoFormIdPattern = /^[A-Za-z0-9]{6}$/u
const addToCartContexts = new Set([
  'collection_quick_add',
  'home_quick_add',
  'cart_you_may_also_like',
  'pdp_form',
  'pdp_sticky_bar',
  'pdp_recommendations',
  'campaign_product',
  'smb_product',
])
const defaultAddToCartSectionTypes = Object.freeze({
  home_quick_add: 'featured-collection',
  cart_you_may_also_like: 'featured-collection',
  collection_quick_add: 'main-collection',
  pdp_form: 'main-product',
  pdp_sticky_bar: 'main-product',
  pdp_recommendations: 'product-recommendations',
})
const pdpFbtSelector =
  'input[type="checkbox"][data-main-product-figma-addon][data-addon-kind="fbt"]'
const headerCategorySelectHookSelector =
  '[data-lp-track][data-lp-track-context][data-lp-track-source="vendor"]'
const selectItemHookSelector = 'a[data-lp-track="select_item"][data-lp-track-source="vendor"]'
const selectItemCardSelector = 'product-card[data-lp-product-id]'
const globalBannerCarouselSelector = '.shopify-section--slideshow slideshow-carousel'
const globalBannerSlideSelector = '.slideshow__slide'
const globalTransientTrackingFields = Object.freeze({
  lp_track: null,
  lp_track_context: null,
  lp_track_source: null,
  lp_tracking_owner: null,
  lp_interaction_method: null,
  lp_section_type: null,
  lp_item_name: null,
  lp_product_id: null,
  lp_variant_id: null,
  lp_quantity: null,
  lp_campaign: null,
  lp_banner_text: null,
  lp_banner_number: null,
  ecommerce: null,
})
const globalEventTrackingDefaults = Object.freeze(
  Object.fromEntries(Object.keys(globalTransientTrackingFields).map((key) => [key, undefined])),
)

function resolveDataLayer(providedDataLayer) {
  if (providedDataLayer) return providedDataLayer
  if (globalThis.dataLayer == null) globalThis.dataLayer = []
  return globalThis.dataLayer
}

function resolveAddHook(form, submitter) {
  const directHook = submitter?.closest?.(addHookSelector) ?? form?.querySelector?.(addHookSelector)
  if (directHook) return directHook

  // Impact moves quick-buy drawers to body; retain the exact originating card.
  const drawerId = form?.closest?.('quick-buy-drawer')?.getAttribute?.('id')
  if (!drawerId) return null
  return (
    Array.from(
      form.ownerDocument?.querySelectorAll?.(addHookSelector + '[aria-controls]') ?? [],
    ).find((hook) => hook.getAttribute('aria-controls') === drawerId) ?? null
  )
}

function resolveHeaderCategorySelectHook(target) {
  const hook = target?.closest?.(headerCategorySelectHookSelector)
  if (!hook) return null
  if (hook.getAttribute?.('data-lp-track-source') !== 'vendor') return null
  if (!HEADER_CATEGORY_SELECT_EVENTS[hook.getAttribute?.('data-lp-track')]) return null
  const context = hook.getAttribute?.('data-lp-track-context')
  if (context !== 'header_desktop_nav' && context !== 'header_mobile_nav') return null
  return hook
}

function headerCategorySelectPayload(hook, interactionMethod) {
  const track = hook.getAttribute?.('data-lp-track')
  const selectEvent = HEADER_CATEGORY_SELECT_EVENTS[track]
  if (!selectEvent) return null

  const payload = {
    ...selectEvent,
    lp_track_context: hook.getAttribute?.('data-lp-track-context'),
    lp_track_source: 'vendor',
    lp_tracking_owner: 'header_category_select',
    lp_interaction_method: interactionMethod,
  }
  const itemName = String(hook.getAttribute?.('data-lp-item-name') ?? hook.textContent ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (itemName) payload.lp_item_name = itemName

  return payload
}

function globalSelectItemPayload(event) {
  if (!event.isTrusted) return null
  const hook = event.target?.closest?.(selectItemHookSelector)
  if (!hook) return null
  const product = hook.closest?.(selectItemCardSelector) ?? hook
  const productId = product.getAttribute?.('data-lp-product-id')
  const variantId = product.getAttribute?.('data-lp-variant-id')
  const itemName = product.getAttribute?.('data-lp-item-name')
  const context = hook.getAttribute?.('data-lp-track-context')
  if (!/^\d+$/.test(productId ?? '') || !/^\d+$/.test(variantId ?? '')) return null
  if (!itemName || !context) return null

  return {
    event: 'lp_select_item',
    lp_track: 'select_item',
    lp_track_context: context,
    lp_track_source: 'vendor',
    lp_tracking_owner: 'global_product_link',
    ecommerce: {
      item_list_id: context,
      items: [
        {
          item_id: `shopify_ZZ_${productId}_${variantId}`,
          item_name: itemName,
        },
      ],
    },
  }
}

function globalBannerPayload(slide, index) {
  if (!slide?.matches?.(globalBannerSlideSelector) || !Number.isInteger(index) || index < 0) {
    return null
  }
  const text = String(slide.querySelector?.('[data-sequence="heading"]')?.textContent ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text) return null

  return {
    event: 'lp_view_homepage_banner',
    lp_track: 'view_homepage_banner',
    lp_track_context: 'homepage_banner',
    lp_track_source: 'vendor',
    lp_tracking_owner: 'global_slideshow_visibility',
    lp_banner_text: text,
    lp_banner_number: index + 1,
  }
}

function headerCategoryHoverSelection(event) {
  if (event.pointerType !== 'mouse') return null

  const hook = resolveHeaderCategorySelectHook(event.target)
  if (!hook || hook.getAttribute?.('data-lp-track-context') !== 'header_desktop_nav') return null
  if (event.relatedTarget?.nodeType != null && hook.contains?.(event.relatedTarget)) return null

  const track = hook.getAttribute?.('data-lp-track')
  if (track === 'select_top_category') {
    if (hook.getAttribute?.('data-lp-menu-trigger') == null) return null
    const menuOpenTrigger =
      hook.closest?.('lp-header')?.getAttribute?.('data-menu-open-trigger') || 'hover'
    if (menuOpenTrigger !== 'hover') return null
  }
  if (track === 'select_second_category' && hook.getAttribute?.('data-lp-category') == null) {
    return null
  }

  return { hook, payload: headerCategorySelectPayload(hook, 'hover') }
}

function hasApprovedSurface(form, context, hook) {
  const drawer = form.closest?.('quick-buy-drawer')
  const isQuickBuySource =
    drawer && hook?.getAttribute?.('aria-controls') === drawer.getAttribute?.('id')
  const surface = isQuickBuySource ? hook : form

  if (context === 'pdp_form' || context === 'pdp_sticky_bar') {
    return Boolean(form.closest?.(mainProductRootSelector))
  }

  if (context === 'collection_quick_add') {
    return (
      (isQuickBuySource ||
        form.parentElement?.matches?.(directQuickAddContainerSelector) === true) &&
      Boolean(surface.closest?.(collectionCardSelector))
    )
  }

  if (context === 'cart_you_may_also_like') {
    return Boolean(surface.closest?.(cartCardSelector))
  }

  if (context === 'home_quick_add') {
    return Boolean(surface.closest?.(homeCardSelector))
  }

  if (context === 'pdp_recommendations') {
    return recommendationCardSelectors.some((selector) => Boolean(surface.closest?.(selector)))
  }

  return context === 'campaign_product' || context === 'smb_product'
}

function addToCartSubmission(event) {
  const form = event.target
  if (!form?.matches?.(productFormSelector)) return null
  if (
    event.submitter?.disabled === true ||
    event.submitter?.getAttribute?.('aria-busy') === 'true'
  ) {
    return null
  }

  const hook = resolveAddHook(form, event.submitter)
  const context = hook?.getAttribute?.('data-lp-track-context')
  if (!addToCartContexts.has(context) || !hasApprovedSurface(form, context, hook)) return null

  return {
    context,
    form,
    // The Ajax response contains the accumulated cart line quantity.
    // Snapshot the requested amount before the successful response arrives.
    quantity: Number(form.elements?.quantity?.value ?? 1),
    sectionType: hook.getAttribute?.('data-lp-section-type'),
    campaign: hook.getAttribute?.('data-lp-campaign'),
  }
}

function addToCartPayload(event, submission) {
  const item = event.detail?.items?.[0]
  if (!item) return null

  const payload = {
    ...ADD_TO_CART_SUCCESS_EVENT,
    lp_track_context: submission.context,
  }
  const itemName = item.product_title ?? item.title
  const variantId = item.variant_id ?? item.id

  if (itemName != null && String(itemName).trim() !== '') {
    payload.lp_item_name = String(itemName).trim()
  }
  if (item.product_id != null) payload.lp_product_id = String(item.product_id)
  if (variantId != null) payload.lp_variant_id = String(variantId)
  if (item.quantity != null && Number.isInteger(submission.quantity) && submission.quantity > 0) {
    payload.lp_quantity = submission.quantity
  }
  const sectionType =
    submission.sectionType != null && String(submission.sectionType).trim() !== ''
      ? String(submission.sectionType).trim()
      : defaultAddToCartSectionTypes[submission.context]
  if (sectionType) {
    payload.lp_section_type = sectionType
  }
  if (submission.campaign != null && String(submission.campaign).trim() !== '') {
    payload.lp_campaign = String(submission.campaign).trim()
  }

  return payload
}

function pdpFbtPayload(event) {
  const input = event.target
  if (!input?.matches?.(pdpFbtSelector)) return null
  if (!input.closest?.(figmaRootSelector)) return null

  const track = input.checked ? 'select_fbt_item' : 'unselect_fbt_item'
  const payload = {
    event: `lp_${track}`,
    lp_track: track,
    lp_track_context: 'pdp_accessories',
    lp_track_source: 'vendor',
    lp_tracking_owner: 'main_product_figma_change',
  }
  const productId = input.getAttribute?.('data-main-product-figma-addon')
  const itemName = input
    .closest?.('[data-role="accessory-card"]')
    ?.querySelector?.('strong')
    ?.textContent?.trim()

  if (productId != null && String(productId).trim() !== '') {
    payload.lp_product_id = String(productId).trim()
  }
  if (itemName) payload.lp_item_name = itemName

  return payload
}

function trustedFaqPayload(event) {
  if (event.isTrusted !== true) return null
  const hook = event.target?.closest?.(faqHookSelector)
  if (!hook) return null

  const payload = { ...FAQ_CLICK_EVENT }
  const context = hook.getAttribute?.('data-lp-track-context')
  const sectionType = hook.getAttribute?.('data-lp-section-type')
  const question = hook.querySelector?.(faqQuestionSelector)
  const itemName = String(
    hook.getAttribute?.('data-lp-item-name') ?? question?.textContent ?? hook.textContent ?? '',
  )
    .replace(/\s+/g, ' ')
    .trim()

  if (context != null && String(context).trim() !== '') {
    payload.lp_track_context = String(context).trim()
  }
  if (sectionType != null && String(sectionType).trim() !== '') {
    payload.lp_section_type = String(sectionType).trim()
  }
  if (itemName) payload.lp_item_name = itemName

  return payload
}

function newsletterSubscribePayload(event) {
  const form = event.target
  if (!form?.matches?.(newsletterFormSelector)) return null
  if (
    event.submitter?.disabled === true ||
    event.submitter?.getAttribute?.('aria-busy') === 'true'
  ) {
    return null
  }

  const hook =
    event.submitter?.closest?.(subscribeHookSelector) ?? form.querySelector?.(subscribeHookSelector)
  if (hook?.disabled === true || hook?.getAttribute?.('aria-busy') === 'true') return null
  if (hook?.getAttribute?.('data-lp-track-context') !== 'footer') return null

  return { ...NEWSLETTER_SUBSCRIBE_INTENT_EVENT }
}

export function startTrackingEventAdapter(
  root = document,
  {
    dataLayer: providedDataLayer,
    enableHeaderCategorySelect = false,
    enableSearchDrawer = false,
    enableTrustedFaq = false,
    enableKlaviyoSubscribe = false,
    enableNewsletterSubscribe = false,
    enableCollectionQuickAdd = false,
    enablePdp = false,
    enableAddToCart = false,
    enablePdpFbt = false,
    klaviyoEventTarget,
  } = {},
) {
  const existing = listenerStates.get(root)
  if (existing) return existing.stop

  const dataLayer = resolveDataLayer(providedDataLayer)
  if (!root?.addEventListener || typeof dataLayer?.push !== 'function') return () => {}

  const addToCartEnabled = enableAddToCart || enableCollectionQuickAdd || enablePdp
  const pdpFbtEnabled = enablePdpFbt || enablePdp
  const pendingAddToCartSubmissions = new WeakMap()
  const hoverSelectedHeaderHooks = new WeakSet()
  const klaviyoPositions = new Map()
  const resolvedKlaviyoEventTarget =
    klaviyoEventTarget ?? (typeof window !== 'undefined' ? window : root)
  let runtimeStoreKey = null
  try {
    runtimeStoreKey = JSON.parse(
      root.querySelector?.('#lp-global-integrations-profile')?.textContent,
    ).runtimeStoreKey
  } catch {
    // Other stores retain the search-drawer lifecycle owner.
  }
  const pushTrackingEvent = (payload) => {
    if (runtimeStoreKey !== 'laserpecker-global') {
      dataLayer.push(payload)
      return
    }

    // GTM keeps data-layer values between events. Scope Global signal values to
    // the current event so a later click or FAQ cannot inherit cart item data.
    dataLayer.push({ ...globalEventTrackingDefaults, ...payload })
    dataLayer.push({ ...globalTransientTrackingFields })
  }
  const jpSearchClickOwner = runtimeStoreKey === 'laserpeckerjp'
  const globalSelectItemEnabled = runtimeStoreKey === 'laserpecker-global'
  const globalBannerCarousel = globalSelectItemEnabled
    ? root.querySelector?.(globalBannerCarouselSelector)
    : null
  const seenGlobalBannerSlides = new WeakSet()
  let globalBannerVisible = false
  let globalBannerObserver = null

  const sendGlobalBanner = (slide, index) => {
    if (!globalBannerVisible || !slide || seenGlobalBannerSlides.has(slide)) return
    const payload = globalBannerPayload(slide, index)
    if (!payload) return
    seenGlobalBannerSlides.add(slide)
    pushTrackingEvent(payload)
  }

  const onGlobalBannerSelect = (event) => {
    if (event.target !== globalBannerCarousel) return
    const { slide, index } = event.detail ?? {}
    // Impact announces selection before it updates .is-selected. Let the legacy
    // banner text variable read the newly selected slide in the same task.
    queueMicrotask(() => sendGlobalBanner(slide, index))
  }

  if (globalBannerCarousel) {
    const Observer = root.defaultView?.IntersectionObserver ?? globalThis.IntersectionObserver
    if (typeof Observer === 'function') {
      globalBannerObserver = new Observer(
        (entries) => {
          globalBannerVisible = entries.some((entry) => entry.intersectionRatio >= 0.5)
          if (globalBannerVisible) {
            sendGlobalBanner(globalBannerCarousel.selectedSlide, globalBannerCarousel.selectedIndex)
          }
        },
        { threshold: 0.5 },
      )
      globalBannerObserver.observe(globalBannerCarousel)
      root.addEventListener('carousel:select', onGlobalBannerSelect)
    }
  }

  const onDialogAfterShow = (event) => {
    if (jpSearchClickOwner) return
    if (!event.target?.matches?.(searchDrawerSelector)) return
    pushTrackingEvent({ ...SEARCH_DRAWER_EVENT })
  }

  const onHeaderSearchClick = (event) => {
    if (!event.isTrusted || !event.target?.closest?.(headerSearchHookSelector)) return
    pushTrackingEvent({ ...SEARCH_DRAWER_EVENT })
  }

  const onHeaderCategoryPointerOver = (event) => {
    const selection = headerCategoryHoverSelection(event)
    if (!selection?.payload) return
    hoverSelectedHeaderHooks.add(selection.hook)
    pushTrackingEvent(selection.payload)
  }

  const onHeaderCategoryPointerOut = (event) => {
    const hook = resolveHeaderCategorySelectHook(event.target)
    if (!hook) return
    if (event.relatedTarget?.nodeType != null && hook.contains?.(event.relatedTarget)) return
    hoverSelectedHeaderHooks.delete(hook)
  }

  const onHeaderCategoryClick = (event) => {
    const hook = resolveHeaderCategorySelectHook(event.target)
    if (!hook || hoverSelectedHeaderHooks.has(hook)) return
    const payload = headerCategorySelectPayload(hook, 'click')
    if (payload) pushTrackingEvent(payload)
  }

  const onGlobalSelectItemClick = (event) => {
    const payload = globalSelectItemPayload(event)
    if (payload) pushTrackingEvent(payload)
  }

  const onTrustedFaqClick = (event) => {
    const payload = trustedFaqPayload(event)
    if (payload) pushTrackingEvent(payload)
  }

  const onKlaviyoForms = (event) => {
    const detail = event.detail
    const formId = detail?.formId
    if (typeof formId !== 'string' || !klaviyoFormIdPattern.test(formId)) return

    if (detail.type === 'open') {
      klaviyoPositions.set(formId, 'popup')
      return
    }

    if (detail.type === 'embedOpen') {
      const host = root.querySelector?.(`.klaviyo-form-${formId}`)
      if (host?.closest?.(klaviyoFooterContainerSelector)) {
        klaviyoPositions.set(formId, 'footer')
      } else {
        klaviyoPositions.delete(formId)
      }
      return
    }

    if (detail.type !== 'submit') return
    const context = klaviyoPositions.get(formId)
    if (!context) return

    klaviyoPositions.delete(formId)
    pushTrackingEvent({ ...KLAVIYO_SUBSCRIBE_SUCCESS_EVENT, lp_track_context: context })
  }

  const onNewsletterSubscribe = (event) => {
    const payload = newsletterSubscribePayload(event)
    if (payload) pushTrackingEvent(payload)
  }

  const onAddToCartSubmit = (event) => {
    const submission = addToCartSubmission(event)
    if (submission) pendingAddToCartSubmissions.set(submission.form, submission)
  }

  const onVariantAdd = (event) => {
    const submission = pendingAddToCartSubmissions.get(event.target)
    if (!submission) return

    pendingAddToCartSubmissions.delete(event.target)
    const payload = addToCartPayload(event, submission)
    if (payload) pushTrackingEvent(payload)
  }

  const onCartError = (event) => {
    pendingAddToCartSubmissions.delete(event.target)
  }

  const onPdpFbtChange = (event) => {
    const payload = pdpFbtPayload(event)
    if (payload) pushTrackingEvent(payload)
  }

  if (enableHeaderCategorySelect) {
    root.addEventListener('pointerover', onHeaderCategoryPointerOver)
    root.addEventListener('pointerout', onHeaderCategoryPointerOut)
    root.addEventListener('click', onHeaderCategoryClick)
  }
  if (globalSelectItemEnabled) root.addEventListener('click', onGlobalSelectItemClick)
  if (enableSearchDrawer) {
    root.addEventListener('dialog:after-show', onDialogAfterShow)
    if (jpSearchClickOwner) root.addEventListener('click', onHeaderSearchClick)
  }
  if (enableTrustedFaq) root.addEventListener('click', onTrustedFaqClick)
  if (enableKlaviyoSubscribe && resolvedKlaviyoEventTarget?.addEventListener) {
    resolvedKlaviyoEventTarget.addEventListener('klaviyoForms', onKlaviyoForms)
  }
  if (enableNewsletterSubscribe) root.addEventListener('submit', onNewsletterSubscribe, true)
  if (addToCartEnabled) {
    root.addEventListener('submit', onAddToCartSubmit, true)
    root.addEventListener('variant:add', onVariantAdd)
    root.addEventListener('cart:error', onCartError)
  }
  if (pdpFbtEnabled) root.addEventListener('change', onPdpFbtChange, true)

  const state = {
    stop() {
      if (listenerStates.get(root) !== state) return
      root.removeEventListener('pointerover', onHeaderCategoryPointerOver)
      root.removeEventListener('pointerout', onHeaderCategoryPointerOut)
      root.removeEventListener('click', onHeaderCategoryClick)
      root.removeEventListener('click', onGlobalSelectItemClick)
      root.removeEventListener('carousel:select', onGlobalBannerSelect)
      globalBannerObserver?.disconnect()
      root.removeEventListener('dialog:after-show', onDialogAfterShow)
      root.removeEventListener('click', onHeaderSearchClick)
      root.removeEventListener('click', onTrustedFaqClick)
      resolvedKlaviyoEventTarget?.removeEventListener?.('klaviyoForms', onKlaviyoForms)
      root.removeEventListener('submit', onNewsletterSubscribe, true)
      root.removeEventListener('submit', onAddToCartSubmit, true)
      root.removeEventListener('variant:add', onVariantAdd)
      root.removeEventListener('cart:error', onCartError)
      root.removeEventListener('change', onPdpFbtChange, true)
      listenerStates.delete(root)
    },
  }
  listenerStates.set(root, state)
  return state.stop
}

if (typeof document !== 'undefined') {
  startTrackingEventAdapter(document, {
    enableHeaderCategorySelect: true,
    enableSearchDrawer: true,
    enableTrustedFaq: true,
    enableKlaviyoSubscribe: true,
    enableNewsletterSubscribe: true,
    enableAddToCart: true,
    enablePdpFbt: true,
  })
}
